import React, { useEffect } from 'react';
import {
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Checkbox,
    ListItemText,
    OutlinedInput,
    FormHelperText,
    Button,
    Box
} from '@mui/material';
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

const DeliveryForm = (props) => {
    const { values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting, masterData, minDate } = props;

    const {
        placeOfDeliveryOptions = [],
        typeOfDeliveryOptions = [],
        deliveryComplicationsOptions = [],
        conductedDeliveryOptions = []
    } = masterData;

    // Handle "None" selection logic for delivery complications
    const handleDeliveryComplicationsChange = (event) => {
        const value = event.target.value;

        // If "None" is selected
        if (value.includes('None')) {
            // If "None" was just added, clear all other selections
            if (!values.delivery_complications.includes('None')) {
                setFieldValue('delivery_complications', ['None']);
            } else {
                // If other options are being selected, remove "None"
                const filtered = value.filter(item => item !== 'None');
                setFieldValue('delivery_complications', filtered);
            }
        } else {
            setFieldValue('delivery_complications', value);
        }

        // Clear "Other" text field if "Other" is deselected
        if (!value.includes('Other')) {
            setFieldValue('delivery_complication_other', '');
        }
    };

    // Reset conditional fields when place_of_delivery changes
    useEffect(() => {
        if (values.place_of_delivery === 'Home') {
            setFieldValue('delivery_complications', []);
            setFieldValue('delivery_complication_other', '');
        } else if (values.place_of_delivery === 'Institutional') {
            setFieldValue('who_conducted_delivery', []);
            setFieldValue('who_conducted_delivery_other', '');
        }
    }, [values.place_of_delivery]);

    // Clear "Other" text field when who_conducted_delivery changes
    useEffect(() => {
        if (values.who_conducted_delivery?.[0] !== 'Other') {
            setFieldValue('who_conducted_delivery_other', '');
        }
    }, [values.who_conducted_delivery]);

    return (
        <Box>
            <Grid container spacing={2} sx={{ alignItems: "flex-start" }}>
                {/* Place of Delivery */}
                <Grid size={6}>
                    <FormControl
                        fullWidth
                        error={touched.place_of_delivery && Boolean(errors.place_of_delivery)}
                    >
                        <InputLabel id="place_of_delivery-label">Place of Delivery <span className="text-danger">*</span></InputLabel>
                        <Select
                            labelId="place_of_delivery-label"
                            id="place_of_delivery"
                            name="place_of_delivery"
                            value={values.place_of_delivery}
                            label={
                                <>
                                    Place of Delivery <span className="text-danger">*</span>
                                </>
                            }
                            onChange={handleChange}
                            onBlur={handleBlur}
                        >
                            {placeOfDeliveryOptions.map((item) => (
                                <MenuItem key={item} value={item}>
                                    {item}
                                </MenuItem>
                            ))}
                        </Select>
                        {touched.place_of_delivery && errors.place_of_delivery && (
                            <FormHelperText>{errors.place_of_delivery}</FormHelperText>
                        )}
                    </FormControl>
                </Grid>

                {/* Note */}
                <Grid size={12}>
                    <p className="note-text" style={{ margin: 0, fontSize: '0.875rem', color: '#666' }}>
                        NOTE: Please enter Date of Delivery correctly. It cannot be modified.
                    </p>
                </Grid>

                {/* Type of Delivery */}
                <Grid size={4}>
                    <FormControl
                        fullWidth
                        error={touched.type_of_delivery && Boolean(errors.type_of_delivery)}
                    >
                        <InputLabel id="type_of_delivery-label">Type of Delivery <span className="text-danger">*</span></InputLabel>
                        <Select
                            labelId="type_of_delivery-label"
                            id="type_of_delivery"
                            name="type_of_delivery"
                            value={values.type_of_delivery}
                            label={
                                <>
                                    Type of Delivery <span className="text-danger">*</span>
                                </>
                            }
                            onChange={handleChange}
                            onBlur={handleBlur}
                        >
                            {typeOfDeliveryOptions.map((item) => (
                                <MenuItem key={item} value={item}>
                                    {item}
                                </MenuItem>
                            ))}
                        </Select>
                        {touched.type_of_delivery && errors.type_of_delivery && (
                            <FormHelperText>{errors.type_of_delivery}</FormHelperText>
                        )}
                    </FormControl>
                </Grid>

                {/* Who Conducted Delivery - Only shown when Place of Delivery is "Home" */}
                {values?.place_of_delivery === 'Home' && (
                    <>
                        <Grid size={4}>
                            <FormControl
                                fullWidth
                                error={touched.who_conducted_delivery && Boolean(errors.who_conducted_delivery)}
                            >
                                <InputLabel id="who_conducted_delivery-label">Who Conducted Delivery <span className="text-danger">*</span></InputLabel>
                                <Select
                                    labelId="who_conducted_delivery-label"
                                    id="who_conducted_delivery"
                                    name="who_conducted_delivery"
                                    value={values.who_conducted_delivery?.[0] || ''}
                                    label={
                                        <>
                                            Who Conducted Delivery <span className="text-danger">*</span>
                                        </>
                                    }
                                    onChange={(e) => {
                                        setFieldValue("who_conducted_delivery", [e.target.value]);
                                        if (e.target.value !== "Other") setFieldValue("who_conducted_delivery_other", "");
                                    }}
                                    onBlur={handleBlur}
                                >
                                    {conductedDeliveryOptions.map((item) => (
                                        <MenuItem key={item} value={item}>
                                            {item}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {touched.who_conducted_delivery && errors.who_conducted_delivery && (
                                    <FormHelperText>{errors.who_conducted_delivery}</FormHelperText>
                                )}
                            </FormControl>
                        </Grid>

                        {/* Who Conducted Delivery Other - Only shown when "Other" is selected */}
                        {values?.who_conducted_delivery?.[0] === 'Other' && (
                            <Grid size={4}>
                                <TextField
                                    fullWidth
                                    id="who_conducted_delivery_other"
                                    name="who_conducted_delivery_other"
                                    label={
                                        <>
                                            Specify Who Conducted Delivery <span className="text-danger">*</span>
                                        </>
                                    }
                                    value={values.who_conducted_delivery_other}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={touched.who_conducted_delivery_other && Boolean(errors.who_conducted_delivery_other)}
                                    helperText={touched.who_conducted_delivery_other && errors.who_conducted_delivery_other}
                                />
                            </Grid>
                        )}
                    </>
                )}

                {/* Delivery Complications - Only shown when Place of Delivery is "Institutional" */}
                {values.place_of_delivery === 'Institutional' && (
                    <>
                        <Grid size={4}>
                            <FormControl
                                fullWidth
                                error={touched.delivery_complications && Boolean(errors.delivery_complications)}
                            >
                                <InputLabel id="delivery_complications-label">Delivery Complications <span className="text-danger">*</span></InputLabel>
                                <Select
                                    labelId="delivery_complications-label"
                                    id="delivery_complications"
                                    name="delivery_complications"
                                    multiple
                                    value={values.delivery_complications}
                                    onChange={handleDeliveryComplicationsChange}
                                    onBlur={handleBlur}
                                    input={<OutlinedInput label={
                                        <>
                                            Delivery Complications <span className="text-danger">*</span>
                                        </>
                                    } />}
                                    renderValue={(selected) => selected.join(', ')}
                                    MenuProps={MenuProps}
                                >
                                    {deliveryComplicationsOptions.map((item) => (
                                        <MenuItem key={item} value={item}>
                                            <Checkbox checked={values.delivery_complications.indexOf(item) > -1} />
                                            <ListItemText primary={item} />
                                        </MenuItem>
                                    ))}
                                </Select>
                                {touched.delivery_complications && errors.delivery_complications && (
                                    <FormHelperText>{errors.delivery_complications}</FormHelperText>
                                )}
                            </FormControl>
                        </Grid>

                        {/* Delivery Complication Other - Only shown when "Other" is selected */}
                        {values.delivery_complications.includes('Other') && (
                            <Grid size={4}>
                                <TextField
                                    fullWidth
                                    id="delivery_complication_other"
                                    name="delivery_complication_other"
                                    label={
                                        <>
                                            Specify Other Complication <span className="text-danger">*</span>
                                        </>
                                    }
                                    value={values.delivery_complication_other}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={touched.delivery_complication_other && Boolean(errors.delivery_complication_other)}
                                    helperText={touched.delivery_complication_other && errors.delivery_complication_other}
                                />
                            </Grid>
                        )}
                    </>
                )}

                {/* Date of Delivery */}
                <Grid size={4}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            views={["year", "month", "day"]}
                            label="Date of Delivery *"
                            format="DD-MM-YYYY"
                            value={values.date_of_delivery ? dayjs(values.date_of_delivery) : null}
                            minDate={minDate ? dayjs(minDate) : undefined}
                            maxDate={dayjs()}
                            onChange={(value) => {
                                setFieldValue('date_of_delivery', value ? value.format('YYYY-MM-DD') : null);
                            }}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    autoComplete: "off",
                                    error: touched.date_of_delivery && Boolean(errors.date_of_delivery),
                                    helperText: touched.date_of_delivery && errors.date_of_delivery,
                                    onBlur: handleBlur,
                                    name: "date_of_delivery",
                                    onDrop: (e) => {
                                        e.preventDefault();
                                        return false;
                                    },
                                },
                            }}
                        />
                    </LocalizationProvider>
                </Grid>

                {/* Submit Button */}
                <Grid size={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'Submitting...' : 'Submit'}
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default DeliveryForm;
